/*
 * VivaNutrition: live Amazon price (and in-copy product box) for affiliate products.
 *
 * Owner decision 2026-09-25: show the Amazon price on the store the same way the blog does, on the PDP, collection
 * cards and home cards.
 *
 * Source: the vn-haf feed on the Hostinger WordPress site (Hostinger Affiliate Marketing Tools data). The feed returns
 * pricing=null once a price is 24h old (vn-haf 0.7.0); then nothing is replaced and the page keeps its fallback text
 * ("See today's price on Amazon" / no price line). If the feed is unreachable, same fallback.
 *
 * Compliance (Owner 2026-10-10; Amazon Associates Program Policies, IP License 2(h) and 2(i)):
 *   - every price figure painted here carries a date/time stamp in the page language, built from the feed's
 *     fetched_at (the time that exact price was fetched): "Price as of 10 Oct 2026, 09:00 UTC";
 *   - the price disclaimer is added once per page, under the "As an Amazon Associate..." line (else at the end of the
 *     main content), and is also the stamp's tooltip;
 *   - a price older than 24h is never painted (the feed already drops it; checked again here);
 *   - a price is painted only when the feed's asin_priced is the ASIN of the page the buy button opens for this
 *     shopper (data-vn-link-asin, set by snippets/vn-amazon-market.liquid); blank = search link = no price;
 *   - nothing from the feed is kept in browser storage (2(h): a client application may not cache the content).
 *
 * Targets:
 *   [data-vn-amazon-price="ASIN"]                  price text: "£135.00 on Amazon" + stamp
 *   .vn-amazon-live[data-vn-amazon-asin="ASIN"]    in-copy box (from the PDP deck block `amazon_live`): image, title,
 *                                                  price + stamp, link = data-vn-href (the product's SHOP link)
 *
 * Market-aware (Owner 2026-10-10): data-country (Liquid localization.country.iso_code) gives the page market: GB/IE
 * 'uk', US 'us', DE AT CH LU 'de', FR BE 'fr', IT 'it', ES PT 'es', NL 'nl', PL 'pl', SE 'se', CA 'ca', anything else
 * 'none' (no price figure is ever painted). A price element may carry data-vn-market; without it the page market
 * applies. The buy box's data-vn-href-from/-to swaps the product's default link in the description for the
 * shopper's own link (UK link on GB, amazon.<tld>/dp/<marketplace ASIN> where that marketplace sells another ASIN).
 * data-lang (Liquid request.locale.iso_code) picks the stamp and disclaimer language (en de es fr it pt).
 */
(function () {
  var script = document.currentScript;
  var FEED = script && script.getAttribute('data-feed');
  if (!FEED || !window.fetch) return;
  var COUNTRY = ((script && script.getAttribute('data-country')) || '').toUpperCase();
  var LANG = ((script && script.getAttribute('data-lang')) || document.documentElement.lang || 'en').slice(0, 2).toLowerCase();
  var ZONES = { GB: 'uk', IE: 'uk', US: 'us', DE: 'de', AT: 'de', CH: 'de', LU: 'de', FR: 'fr', BE: 'fr', IT: 'it',
    ES: 'es', PT: 'es', NL: 'nl', PL: 'pl', SE: 'se', CA: 'ca' };
  var MARKETS = { uk: 1, us: 1, de: 1, fr: 1, it: 1, es: 1, nl: 1, pl: 1, se: 1, ca: 1 };
  var PAGE_MKT = ZONES[COUNTRY] || 'none';
  var MAX_AGE_MS = 24 * 3600 * 1000;
  var NOFIG = "See today's price on Amazon";
  // Stamp and disclaimer (Owner 2026-10-10 wording; policy 2(i)).
  var T = {
    en: { o: "on Amazon", n: "See today's price on Amazon", m: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'], f: function (d, mo, y, t) { return 'Price as of ' + d + ' ' + mo + ' ' + y + ', ' + t + ' UTC'; },
      x: 'Prices and availability are accurate as of the date/time indicated and are subject to change. Any price and availability information displayed on Amazon at the time of purchase will apply.' },
    fr: { o: "sur Amazon", n: "Voir le prix du jour sur Amazon", m: ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'], f: function (d, mo, y, t) { return 'Prix au ' + d + ' ' + mo + ' ' + y + ', ' + t + ' UTC'; },
      x: "Les prix et la disponibilité sont exacts à la date et à l'heure indiquées et peuvent changer. Les informations de prix et de disponibilité affichées sur Amazon au moment de l'achat s'appliqueront." },
    de: { o: "bei Amazon", n: "Aktuellen Preis bei Amazon ansehen", m: ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sept.', 'Okt.', 'Nov.', 'Dez.'], f: function (d, mo, y, t) { return 'Preis vom ' + d + '. ' + mo + ' ' + y + ', ' + t + ' UTC'; },
      x: 'Preise und Verfügbarkeit entsprechen dem angegebenen Datum und der angegebenen Uhrzeit und können sich ändern. Es gelten die Preis- und Verfügbarkeitsangaben, die zum Zeitpunkt des Kaufs auf Amazon angezeigt werden.' },
    es: { o: "en Amazon", n: "Ver el precio de hoy en Amazon", m: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'], f: function (d, mo, y, t) { return 'Precio a fecha de ' + d + ' ' + mo + ' ' + y + ', ' + t + ' UTC'; },
      x: 'Los precios y la disponibilidad son exactos a la fecha y hora indicadas y pueden cambiar. Se aplicará la información de precio y disponibilidad que muestre Amazon en el momento de la compra.' },
    it: { o: "su Amazon", n: "Vedi il prezzo di oggi su Amazon", m: ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'], f: function (d, mo, y, t) { return 'Prezzo al ' + d + ' ' + mo + ' ' + y + ', ' + t + ' UTC'; },
      x: "Prezzi e disponibilità sono accurati alla data e all'ora indicate e possono variare. Si applicano le informazioni su prezzo e disponibilità mostrate su Amazon al momento dell'acquisto." },
    pt: { o: "na Amazon", n: "Ver o preço de hoje na Amazon", m: ['jan.', 'fev.', 'mar.', 'abr.', 'mai.', 'jun.', 'jul.', 'ago.', 'set.', 'out.', 'nov.', 'dez.'], f: function (d, mo, y, t) { return 'Preço a ' + d + ' ' + mo + ' ' + y + ', ' + t + ' UTC'; },
      x: 'Os preços e a disponibilidade são exatos à data e hora indicadas e estão sujeitos a alterações. Aplicam-se as informações de preço e disponibilidade apresentadas na Amazon no momento da compra.' }
  };
  var L = T[LANG] || T.en;
  var known = {}; // key "market:ASIN", this page view only (no browser storage)
  var bb = document.querySelector('.vn-affiliate-buybox');
  var HREF_FROM = bb ? bb.getAttribute('data-vn-href-from') || '' : '';
  var HREF_TO = bb ? bb.getAttribute('data-vn-href-to') || '' : '';
  var BB_PRIMARY = bb ? (bb.getAttribute('data-vn-primary') || '').toUpperCase() : '';
  var BB_LINK = bb && bb.hasAttribute('data-vn-link-asin') ? (bb.getAttribute('data-vn-link-asin') || '').toUpperCase() : null;
  var BB_UK = bb ? (bb.getAttribute('data-vn-asin-uk') || '').toUpperCase() : '';
  var CTA = bb ? bb.querySelector('a.vn-affiliate-buybox__cta-img') : null;
  try { sessionStorage.removeItem('vn-amazon-live:v3'); } catch (e) {} // old client cache (before 2026-10-10)

  // On a PDP, the in-copy box of the product itself takes the buy box's ASIN and link: translated descriptions may
  // still carry an older link or the UK ASIN (2026-10-10 audit), and the box price must belong to the page it opens.
  function alignBoxes() {
    if (!bb || !CTA || !BB_PRIMARY) return;
    var to = CTA.getAttribute('href') || '';
    document.querySelectorAll('.vn-amazon-live[data-vn-amazon-asin]:not([data-vn-done])').forEach(function (el) {
      var a = asinOf(el), h = el.getAttribute('data-vn-href') || '';
      if (a === BB_PRIMARY || (BB_UK && a === BB_UK) || (HREF_FROM && h === HREF_FROM)) {
        el.setAttribute('data-vn-amazon-asin', BB_PRIMARY);
        if (to) el.setAttribute('data-vn-href', to);
      }
    });
  }

  function mktOf(el) {
    var m = (el.getAttribute('data-vn-market') || PAGE_MKT).toLowerCase();
    return MARKETS[m] ? m : 'none';
  }
  function asinOf(el) {
    return (el.getAttribute('data-vn-amazon-price') || el.getAttribute('data-vn-amazon-asin') || '').trim().toUpperCase();
  }
  // The ASIN of the page the shopper's button opens: the element's own data-vn-link-asin, else the buy box's when the
  // element shows the buy box's product; null = unknown (recently viewed popup: same map as its PDP, checked daily).
  function linkOf(el) {
    if (el.hasAttribute('data-vn-link-asin')) return (el.getAttribute('data-vn-link-asin') || '').toUpperCase();
    var a = asinOf(el);
    if (BB_LINK !== null && a && a === BB_PRIMARY) return BB_LINK;
    return null;
  }
  function stamp(iso) {
    var t = Date.parse(iso || '');
    if (!t) return '';
    var d = new Date(t);
    var hh = ('0' + d.getUTCHours()).slice(-2), mm = ('0' + d.getUTCMinutes()).slice(-2);
    return L.f(d.getUTCDate(), L.m[d.getUTCMonth()], d.getUTCFullYear(), hh + ':' + mm);
  }
  // A price that may be shown: fresh (fetched under 24h ago, stamped) and of the ASIN the button opens.
  function usable(p, el) {
    if (!p || !p.pricing || p.stale) return false;
    var t = Date.parse(p.fetched_at || '');
    if (!t || Date.now() - t > MAX_AGE_MS) return false;
    var want = linkOf(el);
    if (want === null) return true;
    return !!want && (p.asin_priced || '').toUpperCase() === want;
  }
  function priceHtml(p) {
    return esc(p.pricing) + ' <small class="vn-amz-on">' + esc(L.o || 'on Amazon') + '</small><span class="vn-amz-stamp" title="' + esc(L.x) + '">' + esc(stamp(p.fetched_at)) + '</span>';
  }
  // Once per page, under the Amazon Associates disclosure (else at the end of the main content).
  var disclaimerDone = false;
  function disclaimer() {
    if (disclaimerDone || document.getElementById('vn-amz-disclaimer')) { disclaimerDone = true; return; }
    if (/\/cart(\/|$)/.test(location.pathname)) { disclaimerDone = true; return; } // Owner 2026-11-03: no price disclaimer on the cart page
    var anchor = document.querySelector('.vn-affiliate-buybox__disclosure, .vn-article-cta__disclosure');
    var p = document.createElement('p');
    p.id = 'vn-amz-disclaimer';
    p.className = 'vn-amz-disclaimer';
    p.textContent = L.x;
    if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(p, anchor.nextSibling);
    else (document.querySelector('main') || document.getElementById('MainContent') || document.body).appendChild(p);
    disclaimerDone = true;
  }
  // The description's default link of this product becomes the shopper's own link (same product, right market).
  function swapLinks() {
    if (!HREF_FROM || !HREF_TO || HREF_FROM === HREF_TO) return;
    document.querySelectorAll('a[href]').forEach(function (a) {
      if (a.getAttribute('href') === HREF_FROM) a.setAttribute('href', HREF_TO);
    });
    document.querySelectorAll('.vn-amazon-live[data-vn-href]').forEach(function (el) {
      if (el.getAttribute('data-vn-href') === HREF_FROM) el.setAttribute('data-vn-href', HREF_TO);
    });
  }

  // Keys "market:ASIN" still needed. Price elements in market 'none' need nothing (no figure is painted);
  // in-copy boxes in market 'none' still need image and title (feed without market, price ignored).
  function keysOnPage() {
    var set = {};
    document.querySelectorAll('[data-vn-amazon-price]:not([data-vn-done])').forEach(function (el) {
      var a = asinOf(el), m = mktOf(el);
      if (m !== 'none' && /^[A-Z0-9]{10}$/.test(a)) set[m + ':' + a] = true;
    });
    document.querySelectorAll('.vn-amazon-live[data-vn-amazon-asin]:not([data-vn-done])').forEach(function (el) {
      var a = asinOf(el);
      if (/^[A-Z0-9]{10}$/.test(a)) set[mktOf(el) + ':' + a] = true;
    });
    return Object.keys(set);
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
  }

  function paint() {
    var painted = false;
    document.querySelectorAll('[data-vn-amazon-price]:not([data-vn-done])').forEach(function (el) {
      var m = mktOf(el);
      if (m === 'none') return;
      var k = m + ':' + asinOf(el);
      if (!(k in known)) return;
      var p = known[k];
      if (!usable(p, el)) {
        // Feed answered but no usable price (none, over 24h, another ASIN): the card says where to see it.
        el.textContent = L.n || NOFIG;
        el.classList.add(el.classList.contains('vn-affiliate-buybox__price') ? 'vn-affiliate-buybox__price--nofig' : 'vn-affiliate-price--nofig');
        el.hidden = false;
        el.setAttribute('data-vn-done', '1');
        return;
      }
      el.innerHTML = priceHtml(p);
      el.classList.remove('vn-affiliate-price--nofig', 'vn-affiliate-buybox__price--nofig');
      el.hidden = false;
      el.setAttribute('data-vn-done', '1');
      painted = true;
    });
    document.querySelectorAll('.vn-amazon-live[data-vn-amazon-asin]:not([data-vn-done])').forEach(function (el) {
      var m = mktOf(el);
      var p = known[m + ':' + asinOf(el)];
      if (!p) return;
      var href = el.getAttribute('data-vn-href') || '';
      var show = m !== 'none' && usable(p, el);
      var price = show ? '<p class="vn-amazon-live__price">' + priceHtml(p) + '</p>' : '';
      // VCR-J-0027 (Owner 2026-10-02): the box carries our deck title (data-vn-title, else the PDP's own product
      // title), not Amazon's listing title, which can carry claims the deck removed on purpose.
      var title = el.getAttribute('data-vn-title') || (script && script.getAttribute('data-product-title')) || p.title;
      var img = p.image ? '<img class="vn-amazon-live__img" src="' + esc(p.image) + '" alt="' + esc(title) + '" loading="lazy">' : '';
      var inner = img + '<div class="vn-amazon-live__body"><p class="vn-amazon-live__title">' + esc(title) + '</p>' + price + '</div>';
      el.innerHTML = href ? '<a class="vn-amazon-live__link" href="' + esc(href) + '" target="_blank" rel="nofollow sponsored noopener">' + inner + '</a>' : inner;
      el.hidden = false;
      el.setAttribute('data-vn-done', '1');
      if (show) painted = true;
    });
    if (painted || document.querySelector('.vn-affiliate-buybox__disclosure')) disclaimer();
  }

  var pending = 0;
  function load() {
    swapLinks();
    alignBoxes();
    var need = keysOnPage().filter(function (k) { return !(k in known); });
    paint();
    if (!need.length || pending) return;
    var byMkt = {};
    need.forEach(function (k) { var i = k.indexOf(':'); (byMkt[k.slice(0, i)] = byMkt[k.slice(0, i)] || []).push(k.slice(i + 1)); });
    Object.keys(byMkt).forEach(function (m) {
      var asins = byMkt[m].slice(0, 50);
      pending++;
      fetch(FEED + '?asins=' + asins.join(',') + (m === 'none' ? '' : '&market=' + m), { credentials: 'omit' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) {
          asins.forEach(function (a) { known[m + ':' + a] = (d && d.products && d.products[a]) || null; });
          paint();
        })
        .catch(function () {})
        .then(function () { pending--; });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', load); else load();
  // Cards added later (filters, infinite scroll, quick view).
  var t;
  new MutationObserver(function () { clearTimeout(t); t = setTimeout(load, 300); })
    .observe(document.documentElement, { childList: true, subtree: true });
})();
